/**
 * SheetToolRail — Iter 178 redesign.
 *
 * The rail is the operator's primary tool surface. It must look like an
 * elite piece of software — Bluebeam / Procore level. The Iter 73/EXP-2A
 * version leaked a "secondary text color on dark background" look that
 * read as low-contrast dev art. This pass:
 *
 *   • hero tools (Measure / Markup / Pin) rendered full ink white, larger
 *     icons, chunky active state with a left accent bar in ForgedOps
 *     Orange — matches the way pro CAD apps signal "you are here".
 *   • secondary tools sized down but still full-ink, so the whole rail
 *     reads as one uniform surface instead of a color gradient.
 *   • rail itself sits inside a slightly raised surface with a subtle
 *     inner shadow, so it feels like a physical panel — not a strip of
 *     buttons welded onto the canvas.
 *   • hover uses a lift + tint, not a background swap, so the state
 *     doesn't feel jittery.
 */
import React from "react";
import {
  MousePointer2, Move, MapPin, PenSquare, Ruler,
  Layers, Hammer, Wrench, GitCompare, FileText, Sun,
} from "lucide-react";

const RAIL_W = 76;
const HIT_PRIMARY = 62;
const HIT_SECONDARY = 52;

export default function SheetToolRail({
  mode,
  onSelect, onPan, onAddPin, onMarkup, onMeasure, onAsset,
  onLayers, onObjects, onRevisions, onDocuments,
  fieldMode, onToggleFieldMode,
  assetOpen,
}) {
  return (
    <div
      data-testid="sheet-tool-rail"
      className="border-r border-[var(--fop-border)] flex flex-col items-center py-3 gap-0.5 relative"
      style={{
        width: RAIL_W,
        minWidth: RAIL_W,
        // Iter 178 — layered surface: base rail tint + inner shadow so the
        // rail reads as a raised panel, not a stripe of buttons.
        background: "linear-gradient(180deg, var(--fop-surface) 0%, var(--fop-surface-2) 100%)",
        boxShadow: "inset -1px 0 0 var(--fop-border), inset 0 1px 0 rgba(255,255,255,0.03)",
      }}
    >
      {/* PRIMARY — the operator's four most-used field placements */}
      <Tool icon={Ruler}     label="Measure" onClick={onMeasure} active={mode === "measure"} primary testid="tool-measure" />
      <Tool icon={PenSquare} label="Markup"  onClick={onMarkup}  active={mode === "markup"}  primary testid="tool-markup" />
      <Tool icon={MapPin}    label="Pin"     onClick={onAddPin}  active={mode === "pin"}     primary testid="tool-pin" />
      {/* Iter 178.4 — Asset tool promoted from the floating overlay into
          the rail. Kills the bottom-left button pileup. */}
      <Tool icon={Hammer}    label="Asset"   onClick={onAsset}   active={!!assetOpen}         primary testid="tool-asset" />
      <Divider />
      {/* NAVIGATION */}
      <Tool icon={MousePointer2} label="Select" onClick={onSelect} active={mode === "select"} testid="tool-select" />
      <Tool icon={Move}          label="Pan"    onClick={onPan}    active={mode === "pan"}    testid="tool-pan" />
      <Divider />
      {/* SECONDARY — inspector panels */}
      <Tool icon={Layers}     label="Layers"  onClick={onLayers}     testid="tool-layers" />
      <Tool icon={Wrench}     label="Objects" onClick={onObjects}    testid="tool-objects" />
      <Tool icon={GitCompare} label="Revs"    onClick={onRevisions}  testid="tool-revisions" />
      <Tool icon={FileText}   label="Docs"    onClick={onDocuments}  testid="tool-documents" />
      <Divider />
      {/* MODE */}
      <Tool icon={Sun} label="Field" onClick={onToggleFieldMode} active={!!fieldMode} testid="tool-field-mode" />
    </div>
  );
}

function Tool({ icon: Icon, label, onClick, active, primary, testid }) {
  const h = primary ? HIT_PRIMARY : HIT_SECONDARY;
  const iconSize = primary ? 22 : 18;
  return (
    <button
      onClick={onClick}
      title={label}
      data-testid={testid}
      data-primary={primary ? "true" : "false"}
      data-active={active ? "true" : "false"}
      className={`group relative flex flex-col items-center justify-center rounded-md transition-all duration-150 w-16 select-none ${
        active
          ? "bg-[var(--fop-primary)]/15 text-[var(--fop-primary)]"
          : "text-[var(--fop-text)] hover:bg-[var(--fop-surface-3)]/70 hover:text-white"
      }`}
      style={{ minHeight: h, height: h, gap: 4 }}
    >
      {/* Active-state accent bar — left edge, ForgedOps Orange. Signals
          "this is your current tool" without repainting the whole button. */}
      {active && (
        <span
          aria-hidden="true"
          className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-r-sm bg-[var(--fop-primary)]"
        />
      )}
      <Icon
        size={iconSize}
        strokeWidth={active ? 2.5 : 2}
        className={active ? "" : "opacity-90 group-hover:opacity-100"}
      />
      <span
        className={`font-mono uppercase tracking-wider leading-none ${
          primary ? "text-[10.5px] font-bold" : "text-[9.5px] font-semibold"
        } ${active ? "" : "opacity-85 group-hover:opacity-100"}`}
      >
        {label}
      </span>
    </button>
  );
}

function Divider() {
  return (
    <div
      className="my-1.5 h-px self-stretch"
      style={{
        marginLeft: 12,
        marginRight: 12,
        background: "linear-gradient(90deg, transparent 0%, var(--fop-border-2) 50%, transparent 100%)",
      }}
    />
  );
}
